import { createContext, useContext, type ReactNode } from 'react';
import { DENY_ALL, type CanAccess } from './access';
import { failHard } from './fail-loud';
import {
  KIT_CATALOG_CANARY_KEY,
  REQUIRED_SERVICE_KEYS,
  REQUIRED_SERVICE_MEMBERS,
  type CompositeServiceKey,
  type ResourceKitServices,
} from './services';

/**
 * Where a host installs the services the kit needs but must not own (#1799,
 * #2165). The contracts themselves live in `./services` — this file is only
 * their React binding, which is why it is one of the two modules allowed to
 * import React at all. The types keep their single home: they are published by
 * the framework-free root export, never re-exported from here.
 */

const ResourceKitContext = createContext<ResourceKitServices | null>(null);

export interface ResourceKitProviderProps {
  services: ResourceKitServices;
  children: ReactNode;
}

/**
 * ONE prop, carrying the whole set. An earlier shape took `canAccess` as its
 * own prop; keeping it alongside `services` would leave two ways to wire the
 * same decision and no answer for which wins, so the set is now indivisible.
 *
 * Missing members are checked HERE, at install time, rather than at each
 * consumer. A `t` that is `undefined` does not crash where it is injected — it
 * crashes at the first render that translates something, which may be a tab
 * nobody opens until a fortnight later. Checking on the way in turns a
 * mis-wired host into a failure at boot, which is the only moment anyone can
 * still read the message.
 *
 * TWO PASSES, IN THIS ORDER. Four services are objects, so a top-level
 * `!= null` is satisfied by `{}` and the fortnight-later crash simply moves one
 * level down. The second pass reads inside them — and it can only run after the
 * first has established that there is an object there to read, which is why the
 * loops are not merged.
 *
 * The top-level message is load-bearing text: the seam's suite matches it per
 * key. The member case gets its OWN wording rather than reusing it, so a
 * half-assembled `storage` never reads as an absent one.
 *
 * THEN A THIRD CHECK, WHICH ASKS A DIFFERENT QUESTION (#2188). The two loops
 * establish that the services were WIRED; the canary establishes that the i18n
 * one was FED. `exists` being present says a host installed a probe, not that
 * the host merged the kit's catalog into the instance that probe reads — and
 * that omission has the same shape as everything else here: it renders a page
 * that LOOKS fine, with `rk.resource.validation.required` sitting where a label
 * belongs. It runs last because it CALLS `services.i18n.exists`, which the
 * member pass is what establishes is there to call.
 *
 * ITS PRECONDITION, WHICH THE MESSAGE STATES. `exists` answers off whatever the
 * probe can see at the moment it is asked, so the merged rows have to be there
 * at the provider's FIRST RENDER. A host that loads its resources over the wire
 * — an i18next HTTP backend, say — has merged the catalog correctly and still
 * gets `false` if it mounts the provider before that load lands, so such a host
 * must mount underneath its own ready signal. This repository is not exposed to
 * it for two independent reasons: `src/i18n/index.ts` passes `resources`
 * inline, and the host provider calls `useTranslation()` before it builds the
 * services (`src/lib/resourceKitServices.tsx::HgsResourceKitProvider`), which
 * suspends until the
 * instance is ready.
 *
 * NOR IS IT ONLY A BOOT CHECK. The call sits in the render body rather than
 * behind a mount effect, so it runs at the first render and on every re-render
 * of the provider — including the re-render a language change causes, since the
 * host's `translate` is rebound off `t` and rebuilds the services object
 * (`src/lib/resourceKitServices.tsx::HgsResourceKitProvider`). With
 * `fallbackLng` off, a host that
 * merged one locale and forgot the other passes here at boot and throws on the
 * language switch instead.
 *
 * ITS MESSAGE NAMES EVERY CAUSE. A `false` from `exists` says the key did not
 * resolve, not why: the host never merged the catalog, or the merge has not
 * loaded yet, or the catalog renamed the row `KIT_CATALOG_CANARY_KEY` points
 * at, in which case every CORRECTLY merged host throws here and "merge it" is
 * the wrong instruction. They are indistinguishable from inside core — it
 * cannot read the catalog (ADR-0035 D1) — so the message LEADS WITH the
 * likeliest cause as a flat instruction ("Merge
 * @resource-kit/ui/locales/{en,zh}.json …"), then the timing precondition, and
 * only then the stale constant, so a reader who has already done the merge is
 * pointed at the constant and at pinning it, instead of re-running an
 * instruction they have already followed.
 */
export function ResourceKitProvider({ services, children }: ResourceKitProviderProps) {
  for (const key of REQUIRED_SERVICE_KEYS) {
    if (services?.[key] == null) failHard(`missing resource-kit service "${key}"`);
  }
  for (const [key, members] of Object.entries(REQUIRED_SERVICE_MEMBERS) as [
    CompositeServiceKey,
    readonly string[],
  ][]) {
    const service = services[key] as unknown as Record<string, unknown>;
    for (const member of members) {
      if (service[member] == null) {
        failHard(`missing resource-kit service member "${key}.${member}"`);
      }
    }
  }
  // `!== true`, not `!`: the two loops above already distrust the declared
  // types by testing `== null` rather than falsiness, because a JS-only host or
  // an `any` cast reaches this code at runtime with whatever it likes. `exists`
  // is declared to return `boolean`
  // (`packages/resource-kit-core/services.ts::I18nProbe.exists`), and the same posture
  // applied here closes the false negative where a probe answering something
  // truthy but not `true` satisfies the canary — most plausibly an `async
  // exists`, whose Promise is always truthy. A probe that cannot answer `true`
  // has not proven the catalog is installed, which is what the message below
  // already says, so this needs no branch of its own.
  if (services.i18n.exists(KIT_CATALOG_CANARY_KEY) !== true) {
    failHard(
      `resource-kit catalog not installed: "${KIT_CATALOG_CANARY_KEY}" does not resolve. ` +
        "Merge @resource-kit/ui/locales/{en,zh}.json into the host i18n instance's " +
        "default namespace. The probe must see the merged rows at the provider's first " +
        'render, so a host that loads its resources asynchronously has to mount ' +
        'ResourceKitProvider after i18n is initialized (behind its ready signal). ' +
        'If the merge is in place and initialized, KIT_CATALOG_CANARY_KEY is stale ' +
        'instead: it no longer names a row the catalog ships, so pin it to a shipped ' +
        'row from your own test suite (this repository does so in i18n-key-parity).',
    );
  }
  return <ResourceKitContext.Provider value={services}>{children}</ResourceKitContext.Provider>;
}

/**
 * The installed services. Missing wiring throws EVERYWHERE — including prod,
 * and unlike `useCanAccess` below there is deliberately no fallback: every
 * service here has a plausible-looking null behaviour (a `t` that echoes its
 * key, a `confirm` that says yes, a `storage` that forgets) that renders a page
 * which LOOKS fine and is wrong. Same policy, and the same reasoning, as the
 * router adapter seam in `router.tsx`.
 */
export function useResourceKit(): ResourceKitServices {
  const services = useContext(ResourceKitContext);
  if (!services) {
    failHard(
      'no ResourceKitProvider installed — wrap the app in <ResourceKitProvider> ' +
        '(the host supplies the services; core owns none of them)',
    );
  }
  return services;
}

/**
 * The injected permission decision. With no provider above, every question is
 * answered DENY: a missing permission wiring must never read as "allowed".
 *
 * This one keeps its fallback where `useResourceKit` has none, because DENY is
 * a real, safe answer to "may this user do it?" — there is no equivalent safe
 * answer to "what is this label in the operator's language?".
 */
export function useCanAccess(): CanAccess {
  return useContext(ResourceKitContext)?.canAccess ?? DENY_ALL;
}
